import { useEffect, useRef, type DependencyList } from "react";
import { loadMotion, type GsapMods } from "@/lib/motion";

/**
 * Run a GSAP/ScrollTrigger setup scoped to a ref, lazily and SSR-safely.
 *
 * The callback only fires in the browser, after the motion libs load, and when
 * the user has not requested reduced motion. All animations are created inside a
 * `gsap.context` so they revert cleanly on unmount (and under StrictMode).
 */
export function useScrollEffect<T extends HTMLElement = HTMLDivElement>(
  setup: (mods: GsapMods, scope: T) => void,
  deps: DependencyList = [],
) {
  const ref = useRef<T>(null);

  useEffect(() => {
    let cancelled = false;
    let ctx: { revert: () => void } | undefined;

    loadMotion().then((mods) => {
      if (cancelled || !mods || !ref.current) return;
      ctx = mods.gsap.context(() => setup(mods, ref.current as T), ref.current);
    });

    return () => {
      cancelled = true;
      ctx?.revert();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return ref;
}
